import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Book, Shield, FileText, Clock, DollarSign, Mail, ExternalLink, CheckCircle, XCircle, AlertCircle, Info } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function Documentation() {
  const [docs, setDocs] = useState(null);
  const [systemInfo, setSystemInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const [docsRes, sysRes] = await Promise.all([
          fetch(`${API_BASE}/api/public/docs`),
          fetch(`${API_BASE}/api/public/system-info`)
        ]);
        
        if (docsRes.ok) {
          setDocs(await docsRes.json());
        }
        if (sysRes.ok) {
          setSystemInfo(await sysRes.json());
        }
      } catch (error) {
        console.error("Failed to fetch documentation:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDocs();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-neutral-500">Loading documentation...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <Book className="w-8 h-8 text-neutral-900" />
            <div>
              <h1 className="text-2xl font-semibold text-neutral-900">Documentation</h1>
              <p className="text-sm text-neutral-500">AI Accountability Standards Platform</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* System Status */}
        {systemInfo && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5" />
                System Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  {systemInfo.email_enabled ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                  )}
                  <span className="text-sm">Email Notifications</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm">Renewal Reminders</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm">Daily Backups</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">v{systemInfo.platform_version}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registry Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Public Registry
            </CardTitle>
            <CardDescription>
              {docs?.registry?.purpose}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-sm mb-2">Visibility Rules</h4>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>declared:</strong> {docs?.registry?.visibility?.declared}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Participants:</strong> {docs?.registry?.visibility?.participants}</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Verification Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Verification
            </CardTitle>
            <CardDescription>
              {docs?.verification?.purpose}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-sm mb-2">Verification Behaviors</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-sm">Valid</span>
                  </div>
                  <p className="text-xs text-neutral-600">{docs?.verification?.behavior?.valid}</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-sm">Expired</span>
                  </div>
                  <p className="text-xs text-neutral-600">{docs?.verification?.behavior?.expired}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span className="font-medium text-sm">Withdrawn</span>
                  </div>
                  <p className="text-xs text-neutral-600">{docs?.verification?.behavior?.withdrawn || docs?.verification?.behavior?.revoked}</p>
                </div>
                <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-4 h-4 text-neutral-600" />
                    <span className="font-medium text-sm">Not Found</span>
                  </div>
                  <p className="text-xs text-neutral-600">{docs?.verification?.behavior?.not_found}</p>
                </div>
              </div>
            </div>
            <div className="p-3 bg-neutral-100 rounded-lg">
              <p className="text-xs font-mono text-neutral-600">URL Format: {docs?.verification?.url_format}</p>
            </div>
          </CardContent>
        </Card>

        {/* declaration States */}
        <Card>
          <CardHeader>
            <CardTitle>declaration States</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {docs?.declaration_states && Object.entries(docs.declaration_states).map(([key, value]) => (
                <div key={key} className="p-3 border rounded-lg">
                  <Badge variant={key === "active" ? "default" : key === "expired" ? "secondary" : "destructive"} className="mb-2">
                    {key}
                  </Badge>
                  <p className="text-sm text-neutral-600">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Rate Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Rate Limits
            </CardTitle>
            <CardDescription>
              API rate limiting protects the platform from abuse
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-sm mb-2">Public Endpoints</h4>
                <ul className="space-y-1 text-sm text-neutral-600">
                  {docs?.rate_limits?.public_endpoints && Object.entries(docs.rate_limits.public_endpoints).map(([key, value]) => (
                    <li key={key} className="flex justify-between">
                      <span className="capitalize">{key}</span>
                      <span className="font-mono text-xs">{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-2">Authenticated Endpoints</h4>
                <ul className="space-y-1 text-sm text-neutral-600">
                  {docs?.rate_limits?.authenticated_endpoints && Object.entries(docs.rate_limits.authenticated_endpoints).map(([key, value]) => (
                    <li key={key} className="flex justify-between">
                      <span className="capitalize">{key}</span>
                      <span className="font-mono text-xs">{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Pricing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {docs?.pricing?.base_declaration && (
                <div className="p-4 border rounded-lg bg-neutral-50">
                  <h4 className="font-medium mb-1">Base declaration</h4>
                  <p className="text-2xl font-bold text-neutral-900">
                    ${docs.pricing.base_declaration.amount}
                  </p>
                  <p className="text-sm text-neutral-500">{docs.pricing.base_declaration.includes}</p>
                </div>
              )}
              {docs?.pricing?.addon_standards && (
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-1">Add-on Standards</h4>
                  <p className="text-2xl font-bold text-neutral-900">
                    ${docs.pricing.addon_standards.amount}
                  </p>
                  <p className="text-sm text-neutral-500">{docs.pricing.addon_standards.description}</p>
                </div>
              )}
              {docs?.pricing?.renewal && (
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-1">Renewal</h4>
                  <p className="text-2xl font-bold text-neutral-900">
                    ${docs.pricing.renewal.amount}
                  </p>
                  <p className="text-sm text-neutral-500">{docs.pricing.renewal.validity} validity</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Contact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className="font-medium text-sm mb-1">Support</h4>
                <p className="text-sm text-neutral-600">{docs?.contact?.support_email}</p>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-1">Website</h4>
                <a href={docs?.contact?.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                  {docs?.contact?.website}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-1">Standards Authority</h4>
                <a href={docs?.contact?.standards_authority} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                  View Standards
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Legal Links */}
        <div className="flex gap-4 text-sm text-neutral-500">
          <Link to="/terms" className="hover:text-neutral-900 hover:underline">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-neutral-900 hover:underline">Privacy Policy</Link>
        </div>
      </main>
    </div>
  );
}
